import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { concat } from '@ember/helper';
import { modifier } from 'ember-modifier';
import { PhX } from 'ember-phosphor-icons';
import type { TOC } from '@ember/component/template-only';

interface ModalHeaderSignature {
  Args: Record<string, never>;
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

const ModalHeader: TOC<ModalHeaderSignature> = <template>
  {{! Bare header text inherits the modal title style; explicit heading classes still take precedence. }}
  <div
    data-test-modal-header
    class="flex-none pl-6 pr-12 py-4 border-b tio-border-default tio-text-lg-semibold"
    ...attributes
  >
    {{yield}}
  </div>
</template>;

interface ModalBodySignature {
  Args: Record<string, never>;
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

const ModalBody: TOC<ModalBodySignature> = <template>
  <div data-test-modal-body class="flex-1 overflow-y-auto px-6 py-4" ...attributes>
    {{yield}}
  </div>
</template>;

interface ModalFooterSignature {
  Args: Record<string, never>;
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

const ModalFooter: TOC<ModalFooterSignature> = <template>
  <div data-test-modal-footer class="flex-none px-6 py-4 border-t tio-border-default" ...attributes>
    {{yield}}
  </div>
</template>;

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '90vw' | 'full';

export type AsideSize = 'xs' | 'sm' | 'md';

/**
 * Per size: the drawer's own max-width, and the space the anchor reserves to
 * its right while it is open.
 *
 * The reserve is what keeps the pair on screen. The drawer is absolutely
 * positioned, so it contributes nothing to the overlay's flex centering — the
 * panel alone stays centered and the drawer hangs off the right edge. That
 * clips at surprisingly wide viewports: an `xl` panel with an `sm` drawer runs
 * off below 1280px, and the `4xl` panel used when a program supplies header
 * HTML clips below 1600px, so a 1440px laptop loses part of the drawer. Adding
 * margin-right equal to the width the drawer adds makes flexbox centre the
 * anchor-plus-margin box, which shifts the panel left by half that width and
 * centres the pair — dropping those thresholds to 944px and 1264px.
 */
const ASIDE_SIZES: Record<AsideSize, { width: string; reserve: string }> = {
  xs: { width: 'max-w-xs', reserve: 'mr-[17rem]' }, // 20rem - 3rem tuck
  sm: { width: 'max-w-sm', reserve: 'mr-[21rem]' }, // 24rem - 3rem tuck
  md: { width: 'max-w-md', reserve: 'mr-[25rem]' }, // 28rem - 3rem tuck
};

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '90vw': 'max-w-[90vw]',
  full: 'w-full max-w-none',
};

/**
 * Dialog overlay with scroll lock, focus entry, Escape/outside dismissal, and named content blocks.
 * Attributes apply to the panel.
 * Set width through size so the panel and aside anchor agree; a panel-only width class would
 * misalign them.
 */
interface ModalSignature {
  Args: {
    /** Controls dialog visibility; closing keeps content mounted for the 300 ms exit transition. */
    isOpen: boolean;
    /** Called when the modal requests to close */
    onClose: () => void;
    /**
     * Max-width of the modal panel @default "md"
     *
     * This is the only supported way to set the panel width — a `class` on the
     * component reaches the overlay, not the panel.
     */
    size?: ModalSize;
    /** Close when clicking the backdrop @default true */
    closeOnOutsideClick?: boolean;
    /** Close when pressing Escape @default true */
    closeOnEscapeKey?: boolean;
    /** Show the default close button in the top-right corner @default true */
    allowCloseButton?: boolean;
    /** Accessible label for the dialog (used as aria-label) */
    ariaLabel?: string;
    /**
     * Expands the `aside` block into a panel beside the modal. Ignored when no
     * `aside` block is passed. @default false
     */
    isAsideOpen?: boolean;
    /** Max-width of the aside panel when open @default "xs" */
    asideSize?: AsideSize;
    /** Accessible label for the aside panel region */
    asideLabel?: string;
    /**
     * Leaves the page usable while the dialog is open: no backdrop, no scroll
     * lock, no `aria-modal`, and clicks outside the panel reach the page. The
     * dialog centres in the viewport minus `--tio-modal-inset-right`, so a page
     * with a docked side panel can keep the two side by side. Escape closes it
     * from anywhere on the page unless a popover or modal is open on top, and it
     * takes that press ahead of a non-modal drawer. Closing returns focus to the
     * element that opened the dialog.
     * @default false
     */
    nonModal?: boolean;
  };
  Blocks: {
    /** Default block — free-form content */
    default?: [];
    /** Named block for header content */
    header?: [];
    /** Named block for body content */
    body?: [];
    /** Named block for footer content */
    footer?: [];
    /**
     * Named block for a companion panel that slides out beside the modal.
     * Collapsed until `@isAsideOpen` is true.
     */
    aside?: [];
  };
  Element: HTMLDivElement;
}

export default class Modal extends Component<ModalSignature> {
  @tracked private _mounted = false;
  @tracked private _visible = false;

  /** Unique ID for this modal instance — used to scope querySelector */
  private modalId = crypto.randomUUID();

  /** Portal target — always renders to document.body (use document.querySelector in tests) */
  get portalDestination(): Element {
    return document.body;
  }

  private get size() {
    return this.args.size ?? 'md';
  }

  private get closeOnOutsideClick() {
    return this.args.closeOnOutsideClick ?? true;
  }

  private get closeOnEscapeKey() {
    return this.args.closeOnEscapeKey ?? true;
  }

  private get showCloseButton() {
    return this.args.allowCloseButton ?? true;
  }

  private get panelSizeClass() {
    return SIZE_CLASSES[this.size];
  }

  private get isFullSize() {
    return this.size === 'full';
  }

  private get isAsideOpen() {
    return this.args.isAsideOpen ?? false;
  }

  private get asideSizeClass() {
    return ASIDE_SIZES[this.args.asideSize ?? 'xs'].width;
  }

  // Space the anchor holds open to the drawer's right while it is out, so the
  // overlay centres the pair instead of letting the drawer overhang.
  private get asideReserveClass() {
    return this.isAsideOpen ? ASIDE_SIZES[this.args.asideSize ?? 'xs'].reserve : '';
  }

  manageFocus = modifier((_element: HTMLElement, [isOpen]: [boolean]) => {
    let rafId: number | undefined;
    let savedScrollY = 0;
    const nonModal = this.args.nonModal === true;
    const opener = document.activeElement;
    // Focus can leave a non-modal dialog for the page, so Escape is heard document-wide; keydowns
    // from inside it stop at handleKeyDown. An open popover or modal on top owns the press.
    const onDocumentKeydown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || !this.closeOnEscapeKey) return;
      if (document.querySelector('.reimagined-popover, [aria-modal="true"]')) return;
      this.args.onClose();
    };

    if (isOpen) {
      this._mounted = true;

      // Lock body scroll
      if (!nonModal) {
        savedScrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${savedScrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.overflow = 'hidden';
      } else {
        document.addEventListener('keydown', onDocumentKeydown);
      }

      rafId = requestAnimationFrame(() => {
        rafId = undefined;
        this._visible = true;

        const dialog = document.querySelector<HTMLElement>(`[data-modal-id="${this.modalId}"]`);
        // Only move focus INTO the dialog. Content that focuses itself on insert
        // does so synchronously, before this frame runs, so focusing the container
        // unconditionally takes focus off it and onto a generic div.
        if (dialog && !dialog.contains(document.activeElement)) {
          dialog.focus({ preventScroll: true });
        }
      });
    } else if (this._mounted) {
      this._visible = false;

      const cleanup = setTimeout(() => {
        this._mounted = false;
      }, 300);

      return () => clearTimeout(cleanup);
    }

    return () => {
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
      document.removeEventListener('keydown', onDocumentKeydown);
      if (nonModal && !this.args.isOpen && opener instanceof HTMLElement && opener.isConnected) {
        const dialog = document.querySelector(`[data-modal-id="${this.modalId}"]`);
        if (dialog?.contains(document.activeElement)) {
          opener.focus({ preventScroll: true });
        }
      }
      // A non-modal dialog never locked scroll, so it must not release a lock another overlay holds.
      if (!nonModal && document.body.style.position === 'fixed') {
        const scrollY = savedScrollY;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      }
    };
  });

  handleBackdropClick = () => {
    if (this.closeOnOutsideClick) {
      this.args.onClose();
    }
  };

  handleKeyDown = (event: KeyboardEvent) => {
    if (this.closeOnEscapeKey && event.key === 'Escape') {
      event.stopPropagation();
      this.args.onClose();
    }
  };

  <template>
    {{! Anchor element for the lifecycle modifier }}
    <div
      class="reimagined-modal-anchor sr-only"
      {{this.manageFocus @isOpen}}
      aria-hidden="true"
    ></div>

    {{#if this._mounted}}
      {{#in-element this.portalDestination insertBefore=null}}
        <div
          data-test-modal
          data-modal-id={{this.modalId}}
          class="tio-row-center fixed z-50 p-4
            {{if @nonModal 'tio-modal--non-modal pointer-events-none' 'inset-0'}}"
          role="dialog"
          aria-modal={{unless @nonModal "true"}}
          aria-label={{@ariaLabel}}
          tabindex="-1"
          {{on "keydown" this.handleKeyDown}}
        >
          {{#if @nonModal}}
            {{! No backdrop: the page behind a non-modal dialog stays usable. }}
          {{else if this.closeOnOutsideClick}}
            {{! template-lint-disable no-nested-interactive }}
            <button
              data-test-modal-backdrop
              type="button"
              class="tio-modal__backdrop absolute inset-0 transition-opacity duration-300 cursor-default
                {{if this._visible 'opacity-100' 'opacity-0'}}"
              aria-label="Close"
              tabindex="-1"
              {{on "click" this.handleBackdropClick}}
            ></button>
          {{else}}
            <div
              data-test-modal-backdrop
              class="tio-modal__backdrop absolute inset-0 transition-opacity duration-300
                {{if this._visible 'opacity-100' 'opacity-0'}}"
              aria-hidden="true"
            ></div>
          {{/if}}

          {{! Repeat panel width on the anchor so the aside tracks its edge without changing standalone modal geometry. }}
          <div
            data-test-modal-anchor
            class="relative w-full transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]
              {{if @nonModal 'pointer-events-auto'}}
              {{this.panelSizeClass}}
              {{if this.isFullSize 'h-full'}}
              {{this.asideReserveClass}}"
          >
            {{! The panel sits above the drawer so the drawer appears to emerge from behind it. }}
            <div
              data-test-modal-panel
              class="relative z-10 w-full
                {{this.panelSizeClass}}
                {{if this.isFullSize 'h-full' 'max-h-[85vh]'}}
                rounded-2xl tio-col-flush tio-bg-surface tio-fg-primary shadow-2xl transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]
                {{if
                  this._visible
                  'opacity-100 scale-100 translate-y-0'
                  'opacity-0 scale-95 translate-y-4'
                }}"
              ...attributes
            >
              {{#if this.showCloseButton}}
                <button
                  data-test-modal-close
                  type="button"
                  class="tio-modal__close absolute top-3 right-3 z-10 p-1.5 rounded-lg tio-fg-disabled hover:tio-fg-secondary hover:tio-bg-sunken transition-colors"
                  {{! template-lint-disable no-bare-strings }}
                  aria-label="Close modal"
                  {{on "click" @onClose}}
                >
                  <PhX @size={{20}} />
                </button>
              {{/if}}

              {{#if (has-block "header")}}
                <ModalHeader>{{yield to="header"}}</ModalHeader>
              {{/if}}

              {{#if (has-block "body")}}
                <ModalBody>{{yield to="body"}}</ModalBody>
              {{/if}}

              {{#if (has-block "footer")}}
                <ModalFooter>{{yield to="footer"}}</ModalFooter>
              {{/if}}

              {{#if (has-block)}}
                {{yield}}
              {{/if}}
            </div>

            {{! The aside tucks 48px beneath the panel; pad content beyond the overlap. It collapses to zero width and is inert when closed. }}
            {{#if (has-block "aside")}}
              <div
                data-test-modal-aside
                class="absolute left-full top-0 h-full -ml-12 overflow-hidden tio-col-flush tio-bg-surface tio-fg-primary rounded-r-2xl transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]
                  {{if
                    this.isAsideOpen
                    (concat 'w-full opacity-100 shadow-2xl ' this.asideSizeClass)
                    'w-0 max-w-0 opacity-0'
                  }}"
                {{! role="region" so @asideLabel actually names a landmark — a
                    plain div's generic role does not expose an accessible name. }}
                role="region"
                aria-label={{@asideLabel}}
                aria-hidden={{unless this.isAsideOpen "true"}}
                inert={{unless this.isAsideOpen "true"}}
              >
                {{yield to="aside"}}
              </div>
            {{/if}}
          </div>
        </div>
      {{/in-element}}
    {{/if}}
  </template>
}

export type { ModalSignature };
